import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import { Card, Form, Row, Col, Button, Spinner, Table } from "react-bootstrap";
import {
  saveEvent,
  editEvent,
  getByEventId,
  deletepackages,
  editpackages,
  savepackage,
  getpackagebyevent,
} from "../store/slices/userSlice";
import { useSearchParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import { saveeventpackagesbulk } from "../services/dbService";

function Events() {
  const [searchParams] = useSearchParams();
  const eventId = searchParams.get("id") || "";

  const [loading, setLoading] = useState(false);
  const [shareableLink, setShareableLink] = useState("");
  const [formData, setFormData] = useState({
    id: 0,
    name: "",
    startdate: "",
    enddate: "",
    starttime: "",
    endtime: "",
    description: "",
    registrationallowed: "",
    status: "",
  });

  const [packages, setPackages] = useState([]);

  // Fetch existing event + packages (if editing)
  useEffect(() => {
    if (eventId) {
      fetchEventDetails(eventId);
      const baseUrl = window.location.origin;
      const eventViewPath = "/eventregistration";
      setShareableLink(`${baseUrl}${eventViewPath}?id=${eventId}`);
    } else {
      setShareableLink("");
    }
  }, [eventId]);

  const copyLinkToClipboard = () => {
    // This already has a try-catch block for the promise, which is good.
    navigator.clipboard
      .writeText(shareableLink)
      .then(() => {
        Swal.fire("Copied!", "Event link copied to clipboard.", "success");
      })
      .catch((err) => {
        console.error("Failed to copy link:", err);
        Swal.fire("Error", "Failed to copy link.", "error");
      });
  };

  const fetchEventDetails = async (id) => {
    setLoading(true);
    try {
      // Fetch event details
      const eventResponse = await getByEventId(id);
      if (eventResponse?.status === 200 && eventResponse.data) {
        setFormData((prev) => ({
          ...prev,
          ...eventResponse.data,
        }));
      } else {
        Swal.fire("Error", "Error fetching event details!", "error");
        // Optionally throw an error or return to prevent further execution if event details failed to load
        return;
      }

      // Fetch packages for the event
      const packagesResponse = await getpackagebyevent(id);
      if (packagesResponse?.status === 200 && Array.isArray(packagesResponse.data)) {
        setPackages(packagesResponse.data);
      } else {
        Swal.fire("Error", "Error fetching event packages!", "error");
      }
    } catch (error) {
      console.error("Error fetching event data:", error);
      Swal.fire("Error", "An unexpected error occurred while fetching event data!", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const addPackage = () => {
    setPackages((prev) => [...prev, { name: "", rate: "", member: "no", startdate: "", enddate: "", type: "All Day", adults: "", kids: "", status: "1" }]);
  };

  const handlePackageChange = (index, field, value) => {
    setPackages((prev) =>
      prev.map((pkg, i) =>
        i === index
          ? {
              ...pkg,
              [field]: value,
            }
          : pkg
      )
    );
  };

  const removePackage = (index) => {
    setPackages((prev) => {
      const updated = [...prev];
      const pkg = updated[index];
      if (pkg.id) {
        updated[index] = { ...pkg, _deleted: true }; // Flag for deletion
      } else {
        updated.splice(index, 1); // It's a new unsaved package, remove it directly from state
      }
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const { name, startdate, enddate, starttime, endtime, description } = formData;

    // Basic validation on event fields
    if (!name || !startdate || !enddate || !starttime || !endtime || !description) {
      setLoading(false);
      Swal.fire("Validation Error", "Please fill in all mandatory event fields.", "warning");
      return;
    }

    // Validate each package row that is not marked for deletion
    for (let i = 0; i < packages.length; i++) {
      const pkg = packages[i];
      if (!pkg._deleted && (!pkg.name || !pkg.rate || !pkg.member || !pkg.startdate || !pkg.enddate || !pkg.type || !pkg.adults || !pkg.kids || !pkg.status)) {
        setLoading(false);
        Swal.fire(
          "Validation Error",
          `Please fill out all fields for package #${i + 1} or remove it.`,
          "warning"
        );
        return;
      }
    }

    try {
      let eventResponse;

      if (formData?.id && formData.id !== 0) {
        // --- Edit Event Flow ---
        eventResponse = await editEvent(formData);
        if (eventResponse?.status === 200) {
          // Process package operations (save, edit, delete)
          const packageOperations = packages.map(async (pkg) => {
            if (pkg._deleted) {
              if (pkg.id) {
                // Only delete if it's an existing package
                try {
                  await deletepackages(pkg.id);
                  console.log(`Package ${pkg.id} deleted successfully.`);
                } catch (deleteError) {
                  console.error(`Failed to delete package ${pkg.id}:`, deleteError);
                  // Decide if you want to fail the whole submission or just log
                  throw new Error(`Failed to delete package: ${pkg.name}`);
                }
              }
              return null; // Skip further processing for deleted packages
            } else if (pkg.id) {
              // Edit existing package
              try {
                await editpackages(pkg);
                console.log(`Package ${pkg.id} updated successfully.`);
              } catch (editError) {
                console.error(`Failed to edit package ${pkg.id}:`, editError);
                throw new Error(`Failed to update package: ${pkg.name}`);
              }
            } else {
              // Save new package
              try {
                pkg.event = formData.id; // Assign event ID to new package
                await savepackage(pkg);
                console.log(`New package "${pkg.name}" saved successfully.`);
              } catch (saveError) {
                console.error(`Failed to save new package "${pkg.name}":`, saveError);
                throw new Error(`Failed to save new package: ${pkg.name}`);
              }
            }
          }).filter(Boolean); // Filter out nulls from deleted packages that don't need API calls

          await Promise.all(packageOperations);
          Swal.fire("Success", "Event and packages updated successfully!", "success");
          // Re-fetch details to ensure UI is consistent after operations
          fetchEventDetails(formData.id);
        } else {
          Swal.fire("Error", "Failed to update event details.", "error");
        }
      } else {
        // --- New Event Save Flow ---
        eventResponse = await saveEvent(formData);
        if (eventResponse?.status === 200 && eventResponse.data?.id) {
          const newEventId = eventResponse.data.id;
          const pkgPayload = packages.map((pkg) => ({
            ...pkg,
            event: newEventId,
          }));

          // Only attempt bulk save if there are packages
          if (pkgPayload.length > 0) {
            const pkgResponse = await saveeventpackagesbulk(pkgPayload);
            if (pkgResponse?.status === 200) {
              Swal.fire("Success", "Event and packages saved successfully!", "success");
              // Clear form and packages after successful new event creation
              setFormData({
                id: 0,
                name: "",
                startdate: "",
                enddate: "",
                starttime: "",
                endtime: "",
                description: "",
                registrationallowed: "",
                status: "",
              });
              setPackages([]);
              // Optionally navigate or show the new link immediately
              setShareableLink(`${window.location.origin}/eventregistration?id=${newEventId}`);
            } else {
              Swal.fire("Error", "Event saved, but failed to save packages.", "error");
            }
          } else {
            Swal.fire("Success", "Event saved successfully (no packages added).", "success");
            setFormData({
              id: 0,
              name: "",
              startdate: "",
              enddate: "",
              starttime: "",
              endtime: "",
              description: "",
              registrationallowed: "",
              status: "",
            });
            setPackages([]);
            setShareableLink(`${window.location.origin}/eventregistration?id=${newEventId}`);
          }
        } else {
          Swal.fire("Error", "Failed to save event.", "error");
        }
      }
    } catch (error) {
      console.error("Submission error:", error);
      Swal.fire("Error", `Something went wrong during submission: ${error.message || "Please try again."}`, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Navbar />
      <Sidebar />
      <div id="main-wrapper">
        <div className="page-wrapper d-block">
          <div className="body-wrapper">
            <div className="container-fluid">
              <div className="row justify-content-center">
                <div className="col-md-10">
                  <Card className="w-100">
                    <Card.Header style={{ background: "#f06969", height: "30px" }}>
                      <h6 className="mb-0" style={{ color: "white" }}>Event Form</h6>
                    </Card.Header>
                    <Card.Body>
                      <Form onSubmit={handleSubmit}>
                        {/* ────────── Event Master Fields ────────── */}
                        <Row>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Event Name *</Form.Label>
                              <Form.Control
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                placeholder="Enter Event Name"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={3}>
                            <Form.Group className="mb-3">
                              <Form.Label>Start Date *</Form.Label>
                              <Form.Control
                                type="date"
                                name="startdate"
                                value={formData.startdate}
                                onChange={handleChange}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={3}>
                            <Form.Group className="mb-3">
                              <Form.Label>End Date *</Form.Label>
                              <Form.Control
                                type="date"
                                name="enddate"
                                value={formData.enddate}
                                onChange={handleChange}
                              />
                            </Form.Group>
                          </Col>
                        </Row>

                        <Row>
                          <Col md={3}>
                            <Form.Group className="mb-3">
                              <Form.Label>Start Time *</Form.Label>
                              <Form.Control
                                type="time"
                                name="starttime"
                                value={formData.starttime}
                                onChange={handleChange}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={3}>
                            <Form.Group className="mb-3">
                              <Form.Label>End Time *</Form.Label>
                              <Form.Control
                                type="time"
                                name="endtime"
                                value={formData.endtime}
                                onChange={handleChange}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={3}>
                            <Form.Group className="mb-3">
                              <Form.Label>Registration Allowed</Form.Label>
                              <Form.Select
                                name="registrationallowed"
                                value={formData.registrationallowed}
                                onChange={handleChange}
                              >
                                <option value="">Select</option>
                                <option value="1">Yes</option>
                                <option value="0">No</option>
                              </Form.Select>
                            </Form.Group>
                          </Col>
                          <Col md={3}>
                            <Form.Group className="mb-3">
                              <Form.Label>Status</Form.Label>
                              <Form.Select
                                name="status"
                                value={formData.status}
                                onChange={handleChange}
                              >
                                <option value="">Select</option>
                                <option value="1">Active</option>
                                <option value="0">Inactive</option>
                              </Form.Select>
                            </Form.Group>
                          </Col>
                        </Row>

                        <Form.Group className="mb-3">
                          <Form.Label>Description *</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={4}
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            placeholder="Enter description"
                          />
                        </Form.Group>

                        {/* ────────── Shareable Link Display ────────── */}
                        {shareableLink && (
                          <Card className="mt-4">
                            <Card.Header style={{ background: "#f06969", height: "30px" }}>
                              <h6 className="mb-0" style={{ color: "white" }}>Shareable Event Link</h6>
                            </Card.Header>
                            <Card.Body className="d-flex align-items-center">
                              <Form.Control type="text" value={shareableLink} readOnly className="me-2" />
                              <Button variant="info" onClick={copyLinkToClipboard}>
                                Copy Link
                              </Button>
                            </Card.Body>
                          </Card>
                        )}

                        {/* ────────── Event Packages Table ────────── */}
                        <Card className="mt-4">
                          <Card.Header style={{ background: "#f06969", height: "30px" }}>
                            <h6 className="mb-0" style={{ color: "white" }}>Event Packages</h6>
                          </Card.Header>
                          <Card.Body>
                            <Table bordered hover responsive>
                              <thead>
                                <tr>
                                  <th style={{ width: "2%", background: "#f06969", color: "white" }}>#</th>
                                  <th style={{ width: "10%", background: "#f06969", color: "white" }}>Name</th>
                                  <th style={{ width: "10%", background: "#f06969", color: "white" }}>Start Date</th>
                                  <th style={{ width: "10%", background: "#f06969", color: "white" }}>End Date</th>
                                  <th style={{ width: "10%", background: "#f06969", color: "white" }}>Type</th>
                                  <th style={{ width: "10%", background: "#f06969", color: "white" }}>Rate</th>
                                  <th style={{ width: "5%", background: "#f06969", color: "white" }}>Member (Yes/No)</th>
                                  <th style={{ width: "5%", background: "#f06969", color: "white" }}>No. of Adults Allowed</th>
                                  <th style={{ width: "5%", background: "#f06969", color: "white" }}>No. of Kids Allowed</th>
                                  <th style={{ width: "10%", background: "#f06969", color: "white" }}>Status</th>
                                  <th style={{ width: "5%", background: "#f06969", color: "white" }}>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {packages
                                  .filter((pkg) => !pkg._deleted) // Only render packages not marked for deletion
                                  .map((pkg, idx) => (
                                    <tr key={pkg.id || `new-${idx}`}>
                                      <td>{idx + 1}</td>
                                      <td>
                                        <Form.Control
                                          as="textarea"
                                          value={pkg.name}
                                          onChange={(e) => handlePackageChange(idx, "name", e.target.value)}
                                          placeholder="Package Name"
                                        />
                                      </td>
                                      <td>
                                        <Form.Control
                                          type="date"
                                          value={pkg.startdate}
                                          onChange={(e) => handlePackageChange(idx, "startdate", e.target.value)}
                                        />
                                      </td>
                                      <td>
                                        <Form.Control
                                          type="date"
                                          value={pkg.enddate}
                                          onChange={(e) => handlePackageChange(idx, "enddate", e.target.value)}
                                        />
                                      </td>
                                      <td>
                                        <Form.Select
                                          value={pkg.type}
                                          onChange={(e) => handlePackageChange(idx, "type", e.target.value)}
                                        >
                                          <option value="All Day">All Day</option>
                                          <option value="Morning">Morning</option>
                                          <option value="Evening">Evening</option>
                                        </Form.Select>
                                      </td>
                                      <td>
                                        <Form.Control
                                          type="number"
                                          value={pkg.rate}
                                          onChange={(e) => handlePackageChange(idx, "rate", e.target.value)}
                                          placeholder="Rate"
                                        />
                                      </td>
                                      <td>
                                        <Form.Select
                                          value={pkg.member}
                                          onChange={(e) => handlePackageChange(idx, "member", e.target.value)}
                                        >
                                          <option value="yes">Yes</option>
                                          <option value="no">No</option>
                                        </Form.Select>
                                      </td>
                                      <td>
                                        <Form.Control
                                          type="number"
                                          value={pkg.adults}
                                          onChange={(e) => handlePackageChange(idx, "adults", e.target.value)}
                                          placeholder="adults"
                                        />
                                      </td>
                                      <td>
                                        <Form.Control
                                          type="number"
                                          value={pkg.kids}
                                          onChange={(e) => handlePackageChange(idx, "kids", e.target.value)}
                                          placeholder="kids"
                                        />
                                      </td>
                                      <td className="text-center">
                                        <Form.Select
                                          value={pkg.status}
                                          onChange={(e) => handlePackageChange(idx, "status", e.target.value)}
                                        >
                                          <option value="1">Active</option>
                                          <option value="0">Inactive</option>
                                        </Form.Select>
                                      </td>
                                      <td>
                                        <Button variant="danger" size="sm" onClick={() => removePackage(idx)}>
                                          Remove
                                        </Button>
                                      </td>
                                    </tr>
                                  ))}

                                {packages.filter((pkg) => !pkg._deleted).length === 0 && (
                                  <tr>
                                    <td colSpan={11} className="text-center">
                                      No packages added yet.
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </Table>

                            <div className="text-end">
                              <Button variant="secondary" size="sm" onClick={addPackage}>
                                + Add Package
                              </Button>
                            </div>
                          </Card.Body>
                        </Card>

                        {/* ────────── Submit Button ────────── */}
                        <div className="mt-4">
                          <Button variant="primary" type="submit" disabled={loading}>
                            {loading ? <Spinner animation="border" size="sm" /> : "Submit"}
                          </Button>
                        </div>
                      </Form>
                    </Card.Body>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Events;